/**
 * Risk-factor signals from a saved note (template selections + clinical text).
 */
import type { QuestionnaireResponse, QuestionnaireResponseItem } from '@medplum/fhirtypes';

const RISK_FACTORS_ITEM_TEXT = /^risk factors?:?$/i;

const PARTICIPANT_ATTENDANCE_LINK_ID = 'participant-attendance';

export interface NoteRiskContext {
  hasRiskFactorsField: boolean;
  selectedRiskFactorCodes: string[];
  clinicalText: string;
}

function isRiskFactorsItemText(text: string | undefined): boolean {
  return RISK_FACTORS_ITEM_TEXT.test((text ?? '').trim());
}

function collectClinicalText(items: QuestionnaireResponseItem[] | undefined, skipAttendance: boolean): string {
  const parts: string[] = [];
  for (const item of items ?? []) {
    if (skipAttendance && item.linkId === PARTICIPANT_ATTENDANCE_LINK_ID) continue;
    for (const answer of item.answer ?? []) {
      if (answer.valueString) parts.push(answer.valueString);
    }
    parts.push(collectClinicalText(item.item, skipAttendance));
  }
  return parts.join('\n');
}

function collectRiskFactorCodes(
  items: QuestionnaireResponseItem[] | undefined,
  codes: Set<string>,
  foundField: { value: boolean },
): void {
  for (const item of items ?? []) {
    if (isRiskFactorsItemText(item.text)) {
      foundField.value = true;
      for (const answer of item.answer ?? []) {
        const code = answer.valueCoding?.code?.trim();
        if (code) codes.add(code);
      }
    }
    collectRiskFactorCodes(item.item, codes, foundField);
  }
}

export function buildNoteRiskContext(
  response: QuestionnaireResponse | null | undefined,
): NoteRiskContext {
  const foundField = { value: false };
  const codes = new Set<string>();
  collectRiskFactorCodes(response?.item, codes, foundField);
  return {
    hasRiskFactorsField: foundField.value,
    selectedRiskFactorCodes: [...codes],
    clinicalText: collectClinicalText(response?.item, true),
  };
}

function textMentions(pattern: RegExp, text: string): boolean {
  return pattern.test(text);
}

export function noteMentionsSubstanceUse(text: string): boolean {
  return textMentions(
    /\b(substance use|substance abuse|increased (alcohol|drug|substance)|alcohol use|drug use|relapse|substance)\b/i,
    text,
  );
}

export function noteMentionsSelfHarm(text: string): boolean {
  return textMentions(/\b(self[- ]?harm|self[- ]?injur)\b/i, text);
}

export function noteMentionsSuicidalIdeation(text: string): boolean {
  return textMentions(/\b(suicidal ideation|suicidal|suicide)\b/i, text);
}

export function noteMentionsHomicidalIdeation(text: string): boolean {
  return textMentions(/\b(homicidal ideation|homicidal|homicide)\b/i, text);
}

export function noteMentionsChildOrElderAbuse(text: string): boolean {
  return textMentions(/\b(child abuse|elder abuse|child or elder|mandated reporter|neglect|elder neglect)\b/i, text);
}

export function noteMentionsIntimatePartnerViolence(text: string): boolean {
  return textMentions(
    /\b(intimate partner violence|domestic violence|ipv|partner violence|intimate partner)\b/i,
    text,
  );
}

export function noteMentionsSafetyPlan(text: string): boolean {
  return textMentions(/\b(safety plan)\b/i, text);
}

export function hasSelectedRiskFactor(ctx: NoteRiskContext, code: string): boolean {
  return ctx.selectedRiskFactorCodes.includes(code);
}

/** True when any risk factor other than "none" is selected. */
export function hasActionableRiskFactorSelection(ctx: NoteRiskContext): boolean {
  return ctx.selectedRiskFactorCodes.some((c) => c !== 'none');
}
